// @flow

import find from 'lodash/find'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import reject from 'lodash/reject'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import type { QuestionRow as QuestionRowType } from 'types/question_row'
import type { QuestionColumn as QuestionColumnType } from 'types/question-column'
import Ui from 'ui'
import QuestionColumn from 'utils/domain/FormBuilder/QuestionColumn'
import QuestionRow from 'utils/domain/FormBuilder/QuestionRow'

import BuilderChoiceProp from './BuilderChoiceProp'

type Props = {
  assignValues?: boolean,
  questionId: string,
  questionColumns: Array<QuestionColumnType>,
  questionRows: Array<QuestionRowType>,
  onUpdateQuestionProps: Function,
  isTable?: boolean,
}

function updatePropHandler(updater: Function, prop: string, value: any) {
  updater({ [prop]: value })
}

function BuilderMatrixEditProps({
  assignValues,
  questionId,
  questionColumns,
  questionRows,
  onUpdateQuestionProps,
  isTable,
}: Props): React.Node {
  const { onReorderQuestionProp } = React.useContext(FormBuilderContext)

  const onAddColumn = React.useCallback(() => {
    const newCol = new QuestionColumn({
      order: questionColumns?.length ?? 0,
      question: questionId,
    })
    updatePropHandler(onUpdateQuestionProps, 'question_columns', [
      ...questionColumns,
      newCol,
    ])
  }, [questionId, questionColumns, onUpdateQuestionProps])

  const onUpdateColumnValue = React.useCallback(
    (id, newValue) => {
      const question_columns = map(questionColumns, (col) =>
        col.id !== id
          ? col
          : {
              ...col,
              text_value: newValue,
              header: newValue,
            }
      )
      updatePropHandler(
        onUpdateQuestionProps,
        'question_columns',
        question_columns
      )
    },
    [questionColumns, onUpdateQuestionProps]
  )

  const onRemoveColumn = React.useCallback(
    (colId) => {
      updatePropHandler(
        onUpdateQuestionProps,
        'question_columns',
        reject(questionColumns, ({ id }) => id === colId)
      )
    },
    [questionColumns, onUpdateQuestionProps]
  )

  const onUpdateColumnNumber = React.useCallback(
    (id, newValue) => {
      const question_columns = map(questionColumns, (col) =>
        col.id !== id ? col : { ...col, number_value: newValue || null }
      )
      updatePropHandler(
        onUpdateQuestionProps,
        'question_columns',
        question_columns
      )
    },
    [questionColumns, onUpdateQuestionProps]
  )

  const onAddRow = React.useCallback(() => {
    const newRow = new QuestionRow({
      order: questionRows?.length ?? 0,
      question: questionId,
    })
    updatePropHandler(onUpdateQuestionProps, 'question_rows', [
      ...questionRows,
      newRow,
    ])
  }, [questionId, questionRows, onUpdateQuestionProps])

  const onUpdateRowValue = React.useCallback(
    (id, newValue) => {
      const question_rows = map(questionRows, (row) =>
        row.id !== id ? row : { ...row, text: newValue }
      )
      updatePropHandler(onUpdateQuestionProps, 'question_rows', question_rows)
    },
    [questionRows, onUpdateQuestionProps]
  )

  const onUpdateRowHelper = React.useCallback(
    (id, newValue) => {
      const question_rows = map(questionRows, (row) =>
        row.id !== id ? row : { ...row, help_text: newValue }
      )
      updatePropHandler(onUpdateQuestionProps, 'question_rows', question_rows)
    },
    [questionRows, onUpdateQuestionProps]
  )

  const onRemoveRow = React.useCallback(
    (rowId) => {
      updatePropHandler(
        onUpdateQuestionProps,
        'question_rows',
        reject(questionRows, ({ id }) => id === rowId)
      )
    },
    [questionRows, onUpdateQuestionProps]
  )

  const rowExtraInputs = React.useCallback(
    (rowId) =>
      isTable
        ? []
        : [
            {
              placeholder: 'Helper text',
              type: 'text',
              onChange: onUpdateRowHelper,
              value: find(questionRows, ({ id }) => id === rowId)?.help_text,
            },
          ],
    [questionRows, onUpdateRowHelper, isTable]
  )

  const onReorder = React.useCallback(
    (result) => {
      if (!result.destination) {
        return
      }

      if (result.destination.index === result.source.index) {
        return
      }

      onReorderQuestionProp(
        questionId,
        result.type,
        null,
        result.source.index,
        result.destination.index
      )
    },
    [onReorderQuestionProp, questionId]
  )

  return (
    <div>
      <DragDropContext onDragEnd={onReorder}>
        <Ui.QuestionTitle>Columns</Ui.QuestionTitle>
        <div>
          <Droppable
            droppableId={`${questionId}_columns`}
            type="question_columns"
          >
            {(providedDrop) => (
              <div ref={providedDrop.innerRef} {...providedDrop.droppableProps}>
                {orderBy(questionColumns, ['order'], ['asc']).map(
                  (column, index) => (
                    <Draggable
                      draggableId={String(column.id)}
                      index={index}
                      key={column.id}
                    >
                      {(providedDrag, snapshotDrag) => (
                        <div
                          ref={providedDrag.innerRef}
                          {...providedDrag.draggableProps}
                          {...providedDrag.dragHandleProps}
                          className={`bg-white rounded border border-white${
                            snapshotDrag.isDragging ? ' shadow' : ''
                          }`}
                        >
                          <BuilderChoiceProp
                            assignValues={assignValues}
                            id={column.id}
                            index={index}
                            key={column.id}
                            numberValue={column.number_value}
                            onRemove={onRemoveColumn}
                            onUpdateNumberValue={onUpdateColumnNumber}
                            onUpdateValue={onUpdateColumnValue}
                            value={column.text_value}
                          />
                        </div>
                      )}
                    </Draggable>
                  )
                )}
                {providedDrop.placeholder}
              </div>
            )}
          </Droppable>

          <Ui.ButtonClear className="border text-sm mt-2" onClick={onAddColumn}>
            Add Column
          </Ui.ButtonClear>
        </div>
        <div className="pt-3">
          <Ui.QuestionTitle>Rows</Ui.QuestionTitle>
          <Droppable droppableId={`${questionId}_rows`} type="question_rows">
            {(providedDrop) => (
              <div ref={providedDrop.innerRef} {...providedDrop.droppableProps}>
                {orderBy(questionRows, ['order'], ['asc']).map((row, index) => (
                  <Draggable
                    draggableId={String(row.id)}
                    index={index}
                    key={row.id}
                  >
                    {(providedDrag, snapshotDrag) => (
                      <div
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        {...providedDrag.dragHandleProps}
                        className={`bg-white rounded border border-white${
                          snapshotDrag.isDragging ? ' shadow' : ''
                        }`}
                      >
                        <BuilderChoiceProp
                          extraInputs={rowExtraInputs(row.id)}
                          id={row.id}
                          index={index}
                          key={row.id}
                          onRemove={onRemoveRow}
                          onUpdateValue={isTable ? () => {} : onUpdateRowValue}
                          value={isTable ? `Row ${index + 1}` : row.text}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {providedDrop.placeholder}
              </div>
            )}
          </Droppable>

          <Ui.ButtonClear className="border text-sm mt-2" onClick={onAddRow}>
            Add Row
          </Ui.ButtonClear>
        </div>
      </DragDropContext>
    </div>
  )
}

export default BuilderMatrixEditProps
